import {memo, useMemo, useState} from 'react';

import styled, {useTheme} from 'styled-components';
// eslint-disable-next-line no-restricted-imports
import {DropdownIndicatorProps, OptionProps, components, StylesConfig, FormatOptionLabelMeta} from 'react-select';

import {
  getCreditCardName,
  paymentCardsIconComponents,
} from '~/shared/utils/payments/paymentMethodsUtils';
import {capitalize} from '~/shared/utils/general';
import {smallBold, smallRegular} from '~/shared/theme/typography';
import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import DropDown from '~/shared/components/DropDown';
import {getLocalizationService} from '~/shared/services/localisationService';

import ChevronDown from '../assets/chevronDown.svg?react';
import Check from '../assets/check.svg?react';
import AddCardIcon from '../assets/addCardIcon.svg?react';

type OptionType = {
  optionType: 'AddCreditCardOption' | 'CreditCard';
  optionId: string;
};
type CreditCardItem = {
  creditcardId: number;
  creditCardType: string;
  cardLastDigits: string;
};
type AddCreditCardOption = OptionType;
type CardOption = CreditCardItem & OptionType;

type CreditCardsDropdownOption = CardOption | AddCreditCardOption;

const isCreditCardOption = (option: OptionType): option is CardOption => option.optionType === 'CreditCard';

const StyledDropdownIcon = styled(ChevronDown)<{$isDropdownOpen?: boolean}>`
  ${({$isDropdownOpen}) => $isDropdownOpen && 'transform: rotate(180deg);'}
  transition: transform linear 0.15s;
`;

const Root = styled.div`
  width: 100%;
  position: relative;
  z-index: 2;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
`;

const OptionLabel = styled.span<{isSelected?: boolean}>`
  display: block;
  ${({isSelected}) => (isSelected ? smallBold : smallRegular)}
`;

const MenuOptionContainer = styled.div<{withBgColor?: boolean}>`
  border-radius: 8px;
  ${({theme, withBgColor}) => withBgColor && `background-color: ${theme.colors.actionWhiteHover};`}
  padding: 0 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const formatOptionLabel = (props: CreditCardsDropdownOption, meta: FormatOptionLabelMeta<CreditCardsDropdownOption>) => {
  const {t} = getLocalizationService();
  if (isCreditCardOption(props)) {
    const {creditCardType, cardLastDigits} = props;

    const CardIcon = paymentCardsIconComponents[capitalize(creditCardType)!];
    const cardName = getCreditCardName({
      paymentMethod: PaymentMethodTypes.CREDIT_CARD,
      isTenbisCredit: false,
      creditCardType,
    });

    return (
      <OptionContainer>
        <CardIcon viewBox="0 0 32 24" width="24" height="18" />
        <OptionLabel
          isSelected={isCreditCardOption(meta.selectValue[0]) && meta.selectValue[0].cardLastDigits === cardLastDigits}
        >
          {capitalize(cardName)} {cardLastDigits}
        </OptionLabel>
      </OptionContainer>
    );
  }

  return (
    <OptionContainer>
      <AddCardIcon />
      <OptionLabel>{t('add_a_credit_card')}</OptionLabel>
    </OptionContainer>
  );
};

const Option = (props: OptionProps<CreditCardsDropdownOption, false>) => {
  const {options, data, children, isSelected} = props;
  return (
    <components.Option {...props}>
      <MenuOptionContainer withBgColor={options.indexOf(data) % 2 === 0}>
        {children}
        {isSelected && <Check />}
      </MenuOptionContainer>
    </components.Option>
  );
};

const StyledDropDown = styled(DropDown).attrs(({$theme}) => ({
  isRtl: !$theme.isLTR,
  formatOptionLabel,
  isSearchable: false,
  omitDefaultStyles: true,
  components: {
    DropdownIndicator: ({selectProps}: DropdownIndicatorProps) =>
      <StyledDropdownIcon $isDropdownOpen={selectProps.menuIsOpen} />,
    Option,
  },
  styles: {
    control: base => ({
      ...base,
      boxShadow: 'none',
      borderRadius: '12px',
      borderColor: `${$theme.colors.borderStrong} !important`,
      cursor: 'pointer',
      paddingLeft: $theme.isLTR ? '8px' : '16px',
      paddingRight: $theme.isLTR ? '16px' : '8px',
    }),
    menu: base => ({
      ...base,
      borderRadius: '16px',
      boxShadow: $theme.shadows.cardElevation,
      background: '#FFF',
      overflow: 'hidden',
      padding: '8px',
    }),
    indicatorSeparator: () => ({
      backgroundColor: 'inherit',
    }),
    option: () => ({
      backgroundColor: '#FFF',
      color: $theme.colors.contentDefault,
    }),
  } as StylesConfig<CreditCardsDropdownOption, false>,
}))``;

type CreditCardsDropdownProps = {
  creditCardList: CreditCardItem[] | undefined;
  selectedCreditCard: number | null;
  setSelectedCreditCard: (creditCardId: number | null) => void;
  addCreditCard: () => void;
  className?: string;
  onClick?: () => void;
};

const CreditCardsDropdown = ({
  creditCardList,
  selectedCreditCard,
  setSelectedCreditCard,
  addCreditCard,
  className,
  onClick,
}: CreditCardsDropdownProps) => {
  const theme = useTheme();

  const optionsList = useMemo(() => ([
    ...(creditCardList?.map(c => ({
      ...c,
      optionType: 'CreditCard',
      optionId: c.creditcardId,
    })) || []),
    {
      optionType: 'AddCreditCardOption',
      optionId: '0',
    },
  ]), [creditCardList]);
  const [currentCard, setCurrentCard] = useState(creditCardList?.length ?
    optionsList.find(o => o.optionId === selectedCreditCard) :
    null,
  );

  const onChange = (option: CreditCardsDropdownOption) => {
    if (isCreditCardOption(option)) {
      setCurrentCard(option);
      setSelectedCreditCard(option.creditcardId);
    }
    if (!isCreditCardOption(option)) {
      addCreditCard();
    }
  };

  if (!currentCard) {
    return null;
  }

  return (
    <Root className={className} onClick={onClick}>
      <StyledDropDown
        name="credit_cards_dropdown"
        $theme={theme}
        options={optionsList}
        value={currentCard}
        onChange={onChange}
        getOptionValue={(option: CreditCardsDropdownOption) => option.optionId}
      />
    </Root>
  );
};

export default memo(CreditCardsDropdown);
